import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LoadingState } from './components/patterns';
import ProtectedRoute from './components/ProtectedRoute';
// Import Layout component - now resolves to index.tsx (no index.ts file)
import Layout from './components/Layout/index';

// Lazy load pages for code splitting
const Login = lazy(() => import('./pages/Login'));
const Callback = lazy(() => import('./pages/Callback'));
const Organizations = lazy(() => import('./pages/Organizations'));
const DataHygiene = lazy(() => import('./pages/DataHygiene'));
const DataHygieneReport = lazy(() => import('./pages/DataHygieneReport'));
const CalculationHygiene = lazy(() => import('./pages/CalculationHygiene'));
const CalculationHygieneReport = lazy(() => import('./pages/CalculationHygieneReport'));
const DuplicateHygieneReport = lazy(() => import('./pages/DuplicateHygieneReport'));
const OrphanedData = lazy(() => import('./pages/OrphanedData'));
const FeatureToggle = lazy(() => import('./pages/FeatureToggle'));
const CodingMetrics = lazy(() => import('./pages/CodingMetrics'));
const WebhookInsights = lazy(() => import('./pages/WebhookInsights'));
const IntegrationStats = lazy(() => import('./pages/IntegrationStats'));
const RepoInsights = lazy(() => import('./pages/RepoInsights'));
const AuthorInsights = lazy(() => import('./pages/AuthorInsights'));
const IncrementalSync = lazy(() => import('./pages/IncrementalSync'));
const JiraSync = lazy(() => import('./pages/JiraSync'));
const AgentSync = lazy(() => import('./pages/AgentSync'));
const AgentMonitoring = lazy(() => import('./pages/AgentMonitoring'));
const AgentMigration = lazy(() => import('./pages/AgentMigration'));
const ActiveSyncs = lazy(() => import('./pages/ActiveSyncs'));
const MissingDataInsights = lazy(() => import('./pages/MissingDataInsights'));
const DataIngestion = lazy(() => import('./pages/DataIngestion'));
const EcsDeployments = lazy(() => import('./pages/EcsDeployments'));
const Migration = lazy(() => import('./pages/Migration'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const OnPremOrganizations = lazy(() => import('./pages/OnPremManagement/OrganizationsPage'));
const OnPremReleases = lazy(() => import('./pages/OnPremManagement/ReleasesPage'));
const OnPremHistory = lazy(() => import('./pages/OnPremManagement/HistoryPage'));
const Insights = lazy(() => import('./pages/Insights'));
const HealthDashboard = lazy(() => import('./pages/HealthDashboard'));
const LegacyOrgDetail = lazy(() => import('./pages/HealthDashboard/LegacyOrgDetail'));
const ExtractOrgDetail = lazy(() => import('./pages/HealthDashboard/ExtractOrgDetail'));
const GitMonitoring = lazy(() => import('./pages/GitMonitoring'));
const GitOrgDetail = lazy(() => import('./pages/GitMonitoring/OrgDetail'));
const LoginAudit = lazy(() => import('./pages/LoginAudit'));
const HivelScoreSync = lazy(() => import('./pages/HivelScoreSync'));
const MakerChecker = lazy(() => import('./pages/MakerChecker'));
const MetricChecker = lazy(() => import('./pages/MetricChecker'));
const AgentRepull = lazy(() => import('./pages/AgentRepull'));

import { useEnvironment } from './hooks/useEnvironment';

// Loading fallback component
const PageLoader = () => <LoadingState message="Loading page..." fullHeight />;

/**
 * Guard component to restrict access to certain routes based on environment/profile
 */
const EnvironmentGuard = ({ children }: { children: React.ReactNode }) => {
  const { isOnPremAllowed } = useEnvironment();
  
  if (!isOnPremAllowed) {
    return <Navigate to="/organizations" replace />;
  }
  
  return <>{children}</>;
};

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes - No authentication required */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/callback"
          element={
            <Suspense fallback={<PageLoader />}>
              <Callback />
            </Suspense>
          }
        />

        {/* Protected routes - Authentication required */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/organizations" replace />} />
          <Route
            path="sitemap"
            element={
              <Suspense fallback={<PageLoader />}>
                <Sitemap />
              </Suspense>
            }
          />
          <Route
            path="organizations"
            element={
              <Suspense fallback={<PageLoader />}>
                <Organizations />
              </Suspense>
            }
          />
          <Route
            path="data-hygiene"
            element={
              <Suspense fallback={<PageLoader />}>
                <DataHygiene />
              </Suspense>
            }
          />
          <Route path="data-hygiene-report/:orgId">
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <DataHygieneReport />
                </Suspense>
              }
            />
            <Route
              path=":tableKey"
              element={
                <Suspense fallback={<PageLoader />}>
                  <DataHygieneReport />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="duplicate-hygiene-report/:orgId"
            element={
              <Suspense fallback={<PageLoader />}>
                <DuplicateHygieneReport />
              </Suspense>
            }
          />
          <Route
            path="data-hygiene/orphaned-data"
            element={
              <Suspense fallback={<PageLoader />}>
                <OrphanedData />
              </Suspense>
            }
          />
          <Route
            path="data-hygiene/calculation"
            element={
              <Suspense fallback={<PageLoader />}>
                <CalculationHygiene />
              </Suspense>
            }
          />
          <Route
            path="calculation-hygiene-report/:orgId"
            element={
              <Suspense fallback={<PageLoader />}>
                <CalculationHygieneReport />
              </Suspense>
            }
          />
          <Route
            path="login-audit"
            element={
              <Suspense fallback={<PageLoader />}>
                <LoginAudit />
              </Suspense>
            }
          />
          <Route
            path="feature-management"
            element={
              <Suspense fallback={<PageLoader />}>
                <FeatureToggle />
              </Suspense>
            }
          />
          <Route
            path="coding-metrics"
            element={
              <Suspense fallback={<PageLoader />}>
                <CodingMetrics />
              </Suspense>
            }
          />
          <Route
            path="webhooks"
            element={
              <Suspense fallback={<PageLoader />}>
                <WebhookInsights />
              </Suspense>
            }
          />
          <Route
            path="integration-stats"
            element={
              <Suspense fallback={<PageLoader />}>
                <IntegrationStats />
              </Suspense>
            }
          />
          <Route
            path="repo-insights"
            element={
              <Suspense fallback={<PageLoader />}>
                <RepoInsights />
              </Suspense>
            }
          />
          <Route
            path="author-insights"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthorInsights />
              </Suspense>
            }
          />
          <Route
            path="incremental-sync"
            element={
              <Suspense fallback={<PageLoader />}>
                <IncrementalSync />
              </Suspense>
            }
          />
          <Route
            path="jira-sync"
            element={
              <Suspense fallback={<PageLoader />}>
                <JiraSync />
              </Suspense>
            }
          />
          <Route
            path="agent-sync"
            element={
              <Suspense fallback={<PageLoader />}>
                <AgentSync />
              </Suspense>
            }
          />
          <Route
            path="agent-monitoring"
            element={
              <Suspense fallback={<PageLoader />}>
                <AgentMonitoring />
              </Suspense>
            }
          />
          <Route
            path="agent-migration"
            element={
              <Suspense fallback={<PageLoader />}>
                <AgentMigration />
              </Suspense>
            }
          />
          <Route
            path="active-syncs"
            element={
              <Suspense fallback={<PageLoader />}>
                <ActiveSyncs />
              </Suspense>
            }
          />
          <Route
            path="missing-data-insights"
            element={
              <Suspense fallback={<PageLoader />}>
                <MissingDataInsights />
              </Suspense>
            }
          />
          <Route
            path="data-ingestion"
            element={
              <Suspense fallback={<PageLoader />}>
                <DataIngestion />
              </Suspense>
            }
          />
          <Route
            path="ecs-deployments"
            element={
              <Suspense fallback={<PageLoader />}>
                <EcsDeployments />
              </Suspense>
            }
          />
          <Route
            path="migration"
            element={
              <Suspense fallback={<PageLoader />}>
                <Migration />
              </Suspense>
            }
          />
          <Route
            path="hivel-score-sync"
            element={
              <Suspense fallback={<PageLoader />}>
                <HivelScoreSync />
              </Suspense>
            }
          />
          <Route
            path="maker-checker"
            element={
              <Suspense fallback={<PageLoader />}>
                <MakerChecker />
              </Suspense>
            }
          />
          <Route
            path="metric-checker"
            element={
              <Suspense fallback={<PageLoader />}>
                <MetricChecker />
              </Suspense>
            }
          />
          <Route
            path="agent-repull"
            element={
              <Suspense fallback={<PageLoader />}>
                <AgentRepull />
              </Suspense>
            }
          />
          <Route
            path="insights"
            element={
              <Suspense fallback={<PageLoader />}>
                <Insights />
              </Suspense>
            }
          />
          <Route path="jira-monitoring">
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <HealthDashboard />
                </Suspense>
              }
            />
            <Route
              path="legacy/:orgId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <LegacyOrgDetail />
                </Suspense>
              }
            />
            <Route
              path="extract/:orgId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ExtractOrgDetail />
                </Suspense>
              }
            />
          </Route>
          <Route path="git-monitoring">
            <Route
              index
              element={
                <Suspense fallback={<PageLoader />}>
                  <GitMonitoring />
                </Suspense>
              }
            />
            <Route
              path=":orgId/:integrationId"
              element={
                <Suspense fallback={<PageLoader />}>
                  <GitOrgDetail />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="on-prem"
            element={
              <EnvironmentGuard>
                <Outlet />
              </EnvironmentGuard>
            }
          >
            <Route index element={<Navigate to="organizations" replace />} />
            <Route
              path="organizations"
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnPremOrganizations />
                </Suspense>
              }
            />
            <Route
              path="releases"
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnPremReleases />
                </Suspense>
              }
            />
            <Route
              path="history"
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnPremHistory />
                </Suspense>
              }
            />
          </Route>
        </Route>

        {/* Catch all - redirect to organizations if authenticated, otherwise to login */}
        <Route path="*" element={<Navigate to="/organizations" replace />} />
      </Routes>
    </Suspense>
  );
}

